/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 拼图按钮组件（使用新的 Redux Slice）
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-12-19 15:43:29
 */
import React, { useMemo } from 'react'
import { Button } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { trackPicsPageClickV2 } from '../../utils/track'
import CONFIG from '../../../../constants/config'
import SelectPhotoTip from '../../../components/SelectPhotoTip'
import actionTypes from '../../../../constants/actionTypes'
import {
  getSelectionConfig,
  normalizeToSet,
  PhotoSelectionSource,
  useActiveSelection
} from '../../utils/selectionHelpers'

interface JigsawButtonProps {
  dataSource?: PhotoSelectionSource
}

const JigsawButton: React.FC<JigsawButtonProps> = ({
  dataSource = 'content'
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    selectionConfig,
    activePhotos: photos,
    activePhotosNum: photosNum,
    activeCheckAll: checkAll,
    activeCheckedPhotos: checkedPhotos,
    activeUnCheckedPhotos: unCheckedPhotos
  } = useActiveSelection(dataSource)

  const disabled = useMemo(
    () =>
      (checkedPhotos.size === 0 || checkedPhotos.size > CONFIG.jigsawMaxNum) &&
      (!checkAll ||
        photosNum - unCheckedPhotos.size === 0 ||
        photosNum - unCheckedPhotos.size > CONFIG.jigsawMaxNum),
    [checkedPhotos.size, checkAll, photosNum, unCheckedPhotos.size]
  )

  const handleJigsawClick = () => {
    trackPicsPageClickV2({ clickItem: 'collage' })

    // 同步数据到旧的 Redux state，以便拼图页面能够读取
    // 1. 同步照片列表（photoSlice 存普通对象）
    dispatch({
      type: actionTypes.SET_PHOTOS,
      data: photos
    })

    // 2. 同步选中状态
    dispatch({
      type: actionTypes.SET_CHECK_ALL,
      data: checkAll
    })

    // 3. 清除旧的选中状态
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })

    // 4. 构建选中的照片 Map 和未选中的照片 Map
    const checkedPhotosMapData: { [key: number]: any } = {}
    const unCheckedPhotosMapData: { [key: number]: any } = {}

    photos.forEach((photo) => {
      const seq = photo.seq
      if (!seq) return

      if (checkAll) {
        // 全选模式：所有照片都选中，除非在 unCheckedPhotos 中
        if (!unCheckedPhotos.has(seq)) {
          checkedPhotosMapData[seq] = photo
        } else {
          unCheckedPhotosMapData[seq] = photo
        }
      } else {
        // 部分选中模式：只在 checkedPhotos 中的才选中
        if (checkedPhotos.has(seq)) {
          checkedPhotosMapData[seq] = photo
        } else {
          unCheckedPhotosMapData[seq] = photo
        }
      }
    })

    // 5. 设置选中的照片
    Object.values(checkedPhotosMapData).forEach((photo) => {
      dispatch({
        type: actionTypes.PUSH_CHECKED_PHOTO,
        data: photo
      })
    })

    // 6. 设置未选中的照片
    Object.values(unCheckedPhotosMapData).forEach((photo) => {
      dispatch({
        type: actionTypes.PUSH_UNCHECKED_PHOTO,
        data: photo
      })
    })

    // 跳转到拼图页面
    navigate('/photo/jigsaw?from=export')
  }

  const btn = (
    <Button
      type="link"
      onClick={handleJigsawClick}
      disabled={disabled}
      style={{ marginRight: 10 }}
    >
      拼图
    </Button>
  )

  return disabled ? (
    <SelectPhotoTip title="请先选择照片，最多可选21张">{btn}</SelectPhotoTip>
  ) : (
    btn
  )
}

export default JigsawButton
